import api, { API_URL } from '../config/axios';

const isBrowser = typeof window !== 'undefined';
const INTERACTIONS_KEY = 'tiktok_interactions_v1';

const normalizeUrl = (url) => (url ? url.replace(/\/+$/, '') : '');

const getMediaBaseUrl = () => {
  const configured = normalizeUrl(API_URL);
  if (configured) {
    return configured;
  }
  if (isBrowser && window.location?.origin) {
    return normalizeUrl(window.location.origin);
  }
  return '';
};

const ABSOLUTE_MEDIA_PROTOCOL = /^(?:https?:|data:|blob:)/i;
const LOCAL_HOSTNAME_PATTERN = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1|\[::1\]|192\.168\.|10\.|172\.(1[6-9]|2\d|3[0-1]))$/i;

const ensureAbsoluteMediaUrl = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  if (ABSOLUTE_MEDIA_PROTOCOL.test(value)) {
    if (/^https?:\/\//i.test(value)) {
      try {
        const parsed = new URL(value);
        if (LOCAL_HOSTNAME_PATTERN.test(parsed.hostname)) {
          const base = getMediaBaseUrl();
          if (base) {
            try {
              const baseUrl = new URL(base, isBrowser && window.location?.origin ? window.location.origin : undefined);
              const path = parsed.pathname.startsWith('/') ? parsed.pathname : `/${parsed.pathname}`;
              return `${baseUrl.origin}${path}${parsed.search || ''}${parsed.hash || ''}`;
            } catch (error) {
              const normalizedBase = base.replace(/\/+$/, '');
              const normalizedPath = parsed.pathname.startsWith('/') ? parsed.pathname : `/${parsed.pathname}`;
              return `${normalizedBase}${normalizedPath}${parsed.search || ''}${parsed.hash || ''}`;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to normalize media URL', value, error);
      }
    }
    return value;
  }
  const base = getMediaBaseUrl();
  if (!base) {
    return value.startsWith('/') ? value : `/${value}`;
  }
  return `${base}/${value.replace(/^\/+/, '')}`;
};

const transformVideoForClient = (video, source = 'api') => {
  if (!video || typeof video !== 'object') {
    return video;
  }

  const result = { ...video, __source: source };

  const primaryUrl = ensureAbsoluteMediaUrl(result.videoUrl || result.url);
  if (primaryUrl) {
    result.videoUrl = primaryUrl;
    result.url = primaryUrl;
  }

  if (result.thumbnail) {
    result.thumbnail = ensureAbsoluteMediaUrl(result.thumbnail);
  }

  if (result.poster) {
    result.poster = ensureAbsoluteMediaUrl(result.poster);
  }

  if (result.transcoded && typeof result.transcoded === 'object') {
    result.transcoded = {
      ...result.transcoded,
      url: ensureAbsoluteMediaUrl(result.transcoded.url),
      thumbnailUrl: ensureAbsoluteMediaUrl(result.transcoded.thumbnailUrl)
    };
  }

  if (result.assets && typeof result.assets === 'object') {
    result.assets = {
      ...result.assets,
      thumbnail: ensureAbsoluteMediaUrl(result.assets.thumbnail)
    };
  }

  if (Array.isArray(result.processedVideos)) {
    result.processedVideos = result.processedVideos.map((item) => ({
      ...item,
      url: ensureAbsoluteMediaUrl(item.url),
      thumbnail: ensureAbsoluteMediaUrl(item.thumbnail)
    }));
  }

  if (result.music && result.music.audioUrl) {
    result.music = {
      ...result.music,
      audioUrl: ensureAbsoluteMediaUrl(result.music.audioUrl)
    };
  }

  if (result.metadata && result.metadata.music && result.metadata.music.audioUrl) {
    result.metadata = {
      ...result.metadata,
      music: {
        ...result.metadata.music,
        audioUrl: ensureAbsoluteMediaUrl(result.metadata.music.audioUrl)
      }
    };
  }

  return result;
};

const normalizeVideos = (list, source) =>
  list.map((video) => transformVideoForClient(video, source));

const extractArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.videos)) return payload.videos;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const unwrapSuccessData = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data;
  }
  return payload;
};

const toFiniteNumber = (value, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

// --- API METHODS ---

export const uploadVideo = async (formData, onUploadProgress) => {
  const UPLOAD_TIMEOUT = 300000; 
  const response = await api.post('/api/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT,
    onUploadProgress
  });
  return response.data;
};

export const getMyVideos = async () => {
  const response = await api.get('/api/upload/my-videos');
  const payload = response.data;
  if (payload && typeof payload === 'object') {
    const next = { ...payload };
    if (Array.isArray(next.videos)) next.videos = normalizeVideos(next.videos, 'api');
    if (Array.isArray(next.data)) next.data = normalizeVideos(next.data, 'api');
    return next;
  }
  return payload;
};
export const getVideosILiked = async () => {
  const response = await api.get("/api/upload/liked-videos");
  const payload = response.data;

  // Check if payload is a valid object
  if (payload && typeof payload === 'object') {
    const next = { ...payload };

    // Support for both 'videos' and 'data' keys from the backend
    if (Array.isArray(next.videos)) {
      next.videos = normalizeVideos(next.videos, 'api');
    }
    
    if (Array.isArray(next.data)) {
      next.data = normalizeVideos(next.data, 'api');
    }

    return next;
  }

  return payload;
};

export const getMyVideo = async (id) => {
  const response = await api.get(`/api/upload/my-videos/${id}`);
  return response.data;
};

export const updateVideo = async (id, data) => {
  const response = await api.put(`/api/upload/my-videos/${id}`, data);
  return response.data;
};

export const deleteVideo = async (id) => {
  const response = await api.delete(`/api/upload/my-videos/${id}`);
  return response.data;
};

export const getVideoAnalytics = async (id) => {
  const response = await api.get(`/api/upload/my-videos/${id}/analytics`);
  return response.data;
};

export const searchMyVideos = async (query) => {
  const response = await api.get(`/api/upload/my-videos/search/${query}`);
  return response.data;
};

export const promoteVideo = async (id) => {
  const response = await api.post(`/api/upload/my-videos/${id}/promote`);
  return response.data;
};

export const getAllVideos = async () => {
  try {
    const response = await api.get('/api/videos');
    const list = extractArray(response.data);
    return normalizeVideos(list, 'api');
  } catch (error) {
    console.error('Failed to load videos from API:', error);
    return [];
  }
};

export const getVideoById = async (id) => {
  const response = await api.get(`/api/videos/${id}`);
  const payload = response.data;
  const video = payload?.data ?? payload;
  return transformVideoForClient(video, 'api');
};

const handleAuthError = (error, defaultMsg) => {
  if (error?.response?.status === 401 || error?.status === 401) {
    const userMessage = error?.userMessage || 
                       error?.response?.data?.userMessage || 
                       error?.response?.data?.message || 
                       defaultMsg;
    const authError = new Error(userMessage);
    authError.status = 401;
    authError.userMessage = userMessage;
    authError.originalError = error;
    throw authError;
  }
  throw error;
};

export const toggleLike = async (id, idempotencyKey) => {
  try {
    const response = await api.post(
      `/api/videos/${id}/like`,
      {},
      { headers: { 'X-Idempotency-Key': idempotencyKey } }
    );
    const payload = unwrapSuccessData(response.data);
    return {
      isLiked: Boolean(payload?.isLiked),
      likes: toFiniteNumber(payload?.likes)
    };
  } catch (error) {
    return handleAuthError(error, 'Please sign in to like videos.');
  }
};

export const shareVideo = async (id, idempotencyKey) => {
  try {
    const response = await api.post(
      `/api/videos/${id}/share`,
      {},
      { headers: { 'X-Idempotency-Key': idempotencyKey } }
    );
    const payload = unwrapSuccessData(response.data);
    return {
      shares: toFiniteNumber(payload?.shares)
    };
  } catch (error) {
    return handleAuthError(error, 'Please sign in to share videos.');
  }
};

export const getVideoComments = async (id) => {
  try {
    const response = await api.get(`/api/videos/${id}/comments`);
    return unwrapSuccessData(response.data);
  } catch (error) {
    console.warn('Unable to fetch comments.', error);
    return [];
  }
};

export const getUserProfile = async (id) => {
  const response = await api.get(`/api/users/${id}`);
  return response.data;
};

export const toggleFollow = async (id) => {
  try {
    const response = await api.post(`/api/users/${id}/follow`);
    const data = response.data || {};
    return {
      isFollowing: data.isFollowing ?? data.following ?? false,
      isPending: data.isPending ?? false,
      ...data
    };
  } catch (error) {
    return handleAuthError(error, 'Please sign in to follow users.');
  }
};

export const getUserVideos = async (id) => {
  try {
    const response = await api.get(`/api/users/${id}/videos`);
    const payload = response.data;
    
    if (Array.isArray(payload)) {
      return { success: true, videos: normalizeVideos(payload, 'api') };
    }
    
    if (payload && typeof payload === 'object') {
      const next = { ...payload };
      const list = next.videos || next.data || [];
      return {
        success: true,
        videos: normalizeVideos(list, 'api'),
        ...next
      };
    }
    return { success: true, videos: [] };
  } catch (error) {
    console.error('Error fetching user videos:', error);
    return { success: false, videos: [], error: error.message };
  }
};

export const updateProfile = async (data) => {
  const response = await api.put('/api/users/profile', data);
  return response.data;
};

export const updatePrivacy = async (data) => {
  const response = await api.put('/api/users/privacy', data);
  return response.data;
};

export const retryVideoProcessing = async (id) => {
  const response = await api.post(`/api/videos/${id}/retry-processing`);
  return response.data;
};

export const downloadVideo = async (id) => {
  const response = await api.get(`/api/videos/${id}/download`, {
    responseType: 'blob'
  });
  const blob = new Blob([response.data], { type: 'video/mp4' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `video-${id}.mp4`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  return { success: true };
};

export const trackVideoView = async (videoId, watchTime = 0, completionRate = 0) => {
  try {
    const response = await api.post('/api/video-views/track', {
      videoId,
      watchTime,
      completionRate
    });
    return response.data;
  } catch (error) {
    console.error('Error tracking video view:', error);
    return { success: false, error: error.message };
  }
};